'use client';

import { useSyncExternalStore } from 'react';

// Next's router changes the URL with history.pushState / replaceState after
// a navigation commits, and the browser fires nothing for those. So the
// store listens to popstate and wraps both methods once, and every reader
// re-renders when the URL moves. Without this a reader would see the old URL
// on the render that a soft navigation triggers and never be told it changed.
const listeners = new Set<() => void>();
let historyPatched = false;

function patchHistory() {
  if (historyPatched || typeof window === 'undefined') return;
  historyPatched = true;
  // Deferred: Next calls pushState from a useInsertionEffect, where React
  // forbids scheduling an update ("useInsertionEffect must not schedule
  // updates"). A microtask runs once that commit has finished.
  let pending = false;
  const notify = () => {
    if (pending) return;
    pending = true;
    queueMicrotask(() => {
      pending = false;
      listeners.forEach((l) => l());
    });
  };
  for (const method of ['pushState', 'replaceState'] as const) {
    const original = window.history[method];
    window.history[method] = function (this: History, ...args) {
      original.apply(this, args);
      notify();
    };
  }
  window.addEventListener('popstate', notify);
}

function subscribe(listener: () => void) {
  patchHistory();
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * The query string from `window.location`, without `useSearchParams`.
 *
 * `useSearchParams` in a client component makes the nearest Suspense boundary
 * a dynamic hole at prerender time (Cache Components), and the app shell's
 * only boundary is the root layout's — so a single read in Main or
 * OnboardingGate pushed every page's content, above-the-fold and all, into
 * the streamed block that a script reveals at the end of the HTML. The
 * server snapshot is `null` (it cannot see the URL), the hydrating render
 * agrees with it, and the real value arrives in the re-render straight after
 * hydration. Only for reads whose pre-hydration default is acceptable (embed
 * chrome, mobile mode, the header's `?compose=`); anything a page's content
 * depends on stays a page-level `searchParams` read.
 */
export function useLocationSearch(): string | null {
  return useSyncExternalStore(
    subscribe,
    () => window.location.search,
    () => null
  );
}

/** One query-string parameter, on the terms of `useLocationSearch`. */
export function useLocationSearchParam(name: string): string | null {
  const search = useLocationSearch();
  return search === null ? null : new URLSearchParams(search).get(name);
}

/**
 * `window.location.pathname`, on the terms of `useLocationSearch`: on a
 * dynamic route, `usePathname` in the app shell is request data and makes
 * the root boundary a dynamic hole, so the shell waited for the request on
 * every /post, /s and /f page.
 */
export function useLocationPathname(): string | null {
  return useSyncExternalStore(
    subscribe,
    () => window.location.pathname,
    () => null
  );
}
